import React, { useMemo, useState } from 'react';
import { DataListRowProps } from './types';
import { HiChevronRight, HiChevronDown } from 'react-icons/hi2';

/**
 * DataListRow - Clean, minimal styling with sticky action column support
 * Sticky columns use CSS position: sticky with right: 0
 */
const DataListRow = React.forwardRef<HTMLTableRowElement, DataListRowProps<any>>(({
    row,
    index,
    columns,
    visibleColumns,
    onClick,
    isLast = false,
    className = '',
    rowHeight = 52,
    isChild = false,
    depth = 0,
    isExpandable = false,
    isExpanded = false,
    onToggleExpand,
    stickyColumns = ['action'],
    childNotPossible = false,
    onRowHover,
}, ref) => {
    const [isLocalHovered, setIsLocalHovered] = useState(false);
    // Separate visible columns into scrollable and sticky
    const { scrollableCols, stickyCols } = useMemo(() => {
        const visibleCols = columns.filter(col => visibleColumns[col.key]);
        const sticky = visibleCols.filter(col => stickyColumns.includes(col.key));
        const scrollable = visibleCols.filter(col => !stickyColumns.includes(col.key));
        return { scrollableCols: scrollable, stickyCols: sticky };
    }, [columns, visibleColumns, stickyColumns]);

    const handleExpandClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onToggleExpand?.();
    };

    const handleRowClick = () => {
        onClick?.(row);
    };

    // Render column content with expand chevron for first column
    const renderColumnContent = (col: typeof columns[0], colIndex: number, isFirstScrollableCol: boolean) => {
        if (isFirstScrollableCol) {
            return (
                <div className="flex items-center gap-2">
                    {isExpandable ? (
                        <button
                            onClick={handleExpandClick}
                            className="flex-shrink-0 w-6 h-6 flex items-center justify-center
                                     text-gray-400 hover:text-primary-light
                                     dark:text-gray-500 dark:hover:text-primary-dark
                                     hover:bg-light-interactive-hover dark:hover:bg-hover-dark
                                     rounded-md transition-colors duration-100"
                        >
                            {isExpanded ? (
                                <HiChevronDown className="w-4 h-4" />
                            ) : (
                                <HiChevronRight className="w-4 h-4" />
                            )}
                        </button>
                    ) : isChild ? (
                        <div
                            className="flex items-center flex-shrink-0"
                            style={{ paddingLeft: `${depth * 16}px` }}
                        >
                            <span className="w-4 h-4 flex items-center justify-center text-primary-light dark:text-primary-dark opacity-60">
                                └
                            </span>
                        </div>
                    ) : (
                        !childNotPossible && <div className="w-6 flex-shrink-0" />
                    )}

                    <div className="flex-1 min-w-0 overflow-visible">
                        {col.render ? col.render(row, index) : (row as any)[col.key]}
                    </div>
                </div>
            );
        }
        return col.render ? col.render(row, index) : (row as any)[col.key];
    };

    return (
        <tr
            ref={ref}
            className={`
                group w-full cursor-pointer relative
                bg-secondary-light dark:bg-secondary-dark
                ${isExpanded && !isChild ? 'bg-hover-light dark:bg-hover-dark' : ''}
                transition-colors duration-100
                hover:bg-hover-light dark:hover:bg-hover-dark
                ${isLocalHovered ? 'hovered-tr' : ''}
                ${!isLast ? 'border-b border-light-border-subtle dark:border-gray-700/50' : ''}
                ${className}
            `}
            style={{ 
                height: `${rowHeight}px`,
            }}
            onClick={handleRowClick}
            onMouseEnter={() => {
                setIsLocalHovered(true);
                onRowHover?.(row);
            }}
            onMouseLeave={() => {
                setIsLocalHovered(false);
                onRowHover?.(null);
            }}
        >
            {/* Scrollable columns */}
            {scrollableCols.map((col, colIndex) => (
                <td
                    key={col.key}
                    className={`
                        px-4 py-3 text-sm text-light-text-primary dark:text-gray-200 align-middle min-w-0
                        ${col.key === 'action' ? 'text-center' : 'text-left'}
                    `}
                    onClick={col.key === 'action' ? (e) => e.stopPropagation() : undefined}
                >
                    {colIndex === 0 && (
                        /* Left accent - visually handled via border or absolute div inside first cell? 
                           Since td can't easily have 'absolute left-0' relative to row for the border accent,
                           we put it inside first cell or use a pseudo-element on tr 
                        */
                        <div
                            className={`absolute left-0 top-0 bottom-0 w-[3px] transition-opacity duration-100 rounded-r
                                ${isChild
                                    ? 'bg-primary-light dark:bg-primary-dark opacity-100'
                                    : 'bg-primary-light dark:bg-primary-dark opacity-0 group-hover:opacity-100'
                                }
                            `}
                        />
                    )}
                    {renderColumnContent(col, colIndex, colIndex === 0)}
                </td>
            ))}

            {/* Sticky columns - fixed on the right */}
            {stickyCols.map((col) => (
                <td
                    key={col.key}
                    className={`
                        px-4 py-3 text-sm text-light-text-primary dark:text-gray-200 align-middle
                        sticky right-0
                        bg-secondary-light dark:bg-secondary-dark
                        group-hover:bg-hover-light dark:group-hover:bg-hover-dark
                        ${isExpanded && !isChild ? '!bg-hover-light dark:!bg-hover-dark' : ''}
                        ${col.key === 'action' ? 'text-center' : 'text-left'}
                    `}
                    style={{
                        boxShadow: '-4px 0 8px -4px rgba(0,0,0,0.08)',
                    }}
                    onClick={col.key === 'action' ? (e) => e.stopPropagation() : undefined}
                >
                    {col.render ? col.render(row, index) : (row as any)[col.key]}
                </td>
            ))}
        </tr>
    );
});

// Explicitly set display name for debugging
DataListRow.displayName = 'DataListRow';

export default DataListRow as <T>(props: DataListRowProps<T> & { ref?: React.Ref<HTMLTableRowElement> }) => JSX.Element;
